/**
 * Location Autocomplete Component
 *
 * Responsive autocomplete input for location search with API suggestions.
 * Works seamlessly on both mobile and desktop using responsive design.
 *
 * Props:
 * - value: Current location input value
 * - onChange: Callback when location changes
 * - onSelect: Callback when a suggestion is selected
 * - placeholder: Input placeholder text
 * - country: Optional country code for filtering suggestions
 * - className: Optional additional CSS classes
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapPin as _MapPin, Loader2, MapPin } from 'lucide-react';
import { getSuggestions } from '../../services/searchService';

function LocationAutocomplete({
  value = '',
  onChange,
  onSelect,
  placeholder = 'Anywhere',
  country,
  className = '',
  debugId: _debugId = 'unknown'
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const debounceTimer = useRef(null);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Track committed value (last valid selection) for restore on blur
  const committedValue = useRef(value);
  const isSelectingRef = useRef(false);
  const isTypingRef = useRef(false);

  /**
   * Fetch suggestions from API with debouncing
   */
  const fetchSuggestions = useCallback(async (query) => {
    if (!query || query.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setLoading(true);

    try {
      const results = await getSuggestions(query, 'location', { country, limit: 5 });
      setSuggestions(results);
      setShowSuggestions(results.length > 0);
    } catch (error) {
      console.error('Location autocomplete error:', error);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setLoading(false);
    }
  }, [country]);

  /**
   * Handle input change with debouncing
   */
  const handleInputChange = (  _e) => {
    const newValue = _e.target.value;

    // Mark that user is actively typing (not an external value change)
    isTypingRef.current = true;

    onChange?.(newValue);
    setSelectedIndex(-1);

    // Clear existing timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new debounced API call
    debounceTimer.current = setTimeout(() => {
      fetchSuggestions(newValue);
    }, 300);
  };

  /**
   * Handle suggestion selection
   */
  const handleSelect = (suggestion) => {
    const selectedText = suggestion.description || suggestion.label || suggestion.display_name || suggestion.name;

    // Mark that we're in the middle of selecting
    isSelectingRef.current = true;

    // Reset typing flag since this is a completed selection
    isTypingRef.current = false;

    // Store as committed value
    committedValue.current = selectedText;

    if (onChange) {
      onChange(selectedText);
    }
    if (onSelect) {
      onSelect(suggestion);
    }

    setShowSuggestions(false);
    setSuggestions([]);
    setSelectedIndex(-1);

    // Reset selecting flag after a short delay
    setTimeout(() => {
      isSelectingRef.current = false;
    }, 100);
  };

  /**
   * Handle keyboard navigation
   */
  const handleKeyDown = (  _e) => {
    if (!showSuggestions || suggestions.length === 0) {return;}

    switch (_e.key) {
      case 'ArrowDown':
        _e.preventDefault();
        setSelectedIndex(prev =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;

      case 'ArrowUp':
        _e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;

      case 'Enter':
        _e.preventDefault();
        // eslint-disable-next-line security/detect-object-injection -- safe: selectedIndex is controlled numeric index
        if (selectedIndex >= 0 && suggestions[selectedIndex]) {
          // eslint-disable-next-line security/detect-object-injection -- safe: selectedIndex is controlled numeric index
          handleSelect(suggestions[selectedIndex]);
        }
        break;

      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;

      default:
        break;
    }
  };

  /**
   * Sync committed value when value prop changes externally (not from user typing)
   */
  useEffect(() => {
    // Only update committedValue if change is from external source (URL sync, restore)
    if (!isTypingRef.current) {
      // Clear committedValue if value is empty (location cleared from pill/URL)
      if (!value) {
        committedValue.current = '';
      } else if (value !== committedValue.current) {
        committedValue.current = value;
      }
    }
  }, [value]);

  /**
   * Close suggestions when clicking outside
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        inputRef.current && !inputRef.current.contains(event.target) &&
        suggestionsRef.current && !suggestionsRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /**
   * Cleanup debounce timer on unmount
   */
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  /**
   * Scroll selected item into view
   */
  useEffect(() => {
    if (selectedIndex >= 0 && suggestionsRef.current) {
      // eslint-disable-next-line security/detect-object-injection -- safe: selectedIndex is controlled numeric index
      const selectedElement = suggestionsRef.current.children[selectedIndex];
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [selectedIndex]);

  return (
    <div className={`relative ${className}`}>
      {/* Input Field */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          id="location-search"
          name="location"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            // Clear field on focus if it has a value (ready for new input)
            if (value && value.trim()) {
              onChange?.('');
              setSuggestions([]);
            } else if (suggestions.length > 0) {
              setShowSuggestions(true);
            }
          }}
          onBlur={() => {
            // Don't restore if we're in the middle of selecting a suggestion
            if (isSelectingRef.current) {
              return;
            }

            // Restore committed value if user didn't complete a new selection
            setTimeout(() => {
              // If value doesn't match committed value, restore it
              if (value !== committedValue.current && committedValue.current) {
                // Reset typing flag before restoring (so useEffect can update committedValue)
                isTypingRef.current = false;
                onChange?.(committedValue.current);
              }
            }, 200);
          }}
          placeholder={placeholder}
          className="w-full text-sm text-gray-900 placeholder-gray-600 bg-transparent border-none outline-none focus:ring-0 p-0"
          autoComplete="off"
          aria-label="Search location"
        />
        {loading && (
          <div className="absolute right-0 top-1/2 -translate-y-1/2">
            <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
          </div>
        )}
      </div>

      {/* Suggestions Dropdown - Responsive */}
      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-60 overflow-y-auto"
        >
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion.id || index}
              type="button"
              onClick={(  _e) => {
                _e.preventDefault();
                _e.stopPropagation();
                handleSelect(suggestion);
              }}
              onMouseDown={(  _e) => {
                _e.preventDefault();
              }}
              className={`w-full flex items-start gap-3 px-4 py-3 text-left transition hover:bg-gray-50 ${
                index === selectedIndex ? 'bg-blue-50 border-l-2 border-l-blue-500' : ''
              } ${
                index === 0 ? 'rounded-t-lg' : ''
              } ${
                index === suggestions.length - 1 ? 'rounded-b-lg' : 'border-b border-gray-100'
              }`}
            >
              <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">
                  {suggestion.description || suggestion.label || suggestion.display_name || suggestion.name}
                </div>
                {(suggestion.types?.[0] || suggestion.type) && (
                  <div className="text-xs text-gray-500 mt-0.5 capitalize">
                    {suggestion.types?.[0] || suggestion.type}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default LocationAutocomplete;
